/**
 * 需要想定 daily 編集グリッドの中核ロジック hook。
 *
 * editRows (baseline + 現在値) を所有し、per-koma 編集変換ハンドラと一括操作
 * (リセット・過去実績採用・rescale・トレンド反映) を index.tsx から分離する薄い
 * wiring。最高 risk の 2 クラスタは deep module に切り出して単体テスト可能にした
 * (architecture review C4):
 *   - save 並行性 / 楽観ロック → `useDraftSaveQueue` (直列化 saveRunRef /
 *     editRowsRef sync-snapshot / ABORTED→conflict をここに集約)
 *   - 参照列採用の undo state machine → `useAdoptReferenceColumn`
 * dirty/導出クラスタは `useForecastDirtyState` へ分離済み。本 hook はこれら 3 hook
 * を editRows を軸に合成し、破棄/衝突解消/取込のような複数クラスタ横断の
 * orchestration だけを持つ。
 *
 * 明示保存モデル (設計書 demand-forecast-explicit-save §5): 編集操作は全て
 * frontend の未保存バッファ (editRows の現在値 6 field) に積まれるだけで、
 * 自動保存タイマー (旧 debounce / retry) は撤去済み。dirty は
 * `computeForecastDiff(baseline, current)` の導出値であり、保存 (executeSaveAll)
 * が成功した小売だけ baseline を現在値へ更新して clean に戻す。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  DemandForecastDailyPage,
  RetailerCorrectedSlots,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { protoDateToCivilDate } from '~/models/civilDate'
import {
  gateCloseCountdownOn,
  gateCloseFrontierFromProto,
} from '~/models/gateClose'
import {
  type AggregateBreakdown,
  applyProportionalToTotal,
  distributeAggregateSlots,
  rescaleAggregateToPeak,
} from '~/pages/DemandForecastDaily/models/aggregateEdit'
import { canMutate } from '~/pages/DemandForecastDaily/models/canMutate'
import { protoRetailerToEditRow } from '~/pages/DemandForecastDaily/models/editRow'
import {
  fieldsSnapshotOf,
  isRowDirty,
} from '~/pages/DemandForecastDaily/models/forecastDirtyState'
import { deriveAggregateEdit } from '~/pages/DemandForecastDaily/models/forecastGates'
import {
  applyProportional,
  calcBgTotalSlots,
  markEditableKomasManual,
  protoSlotsToKoma,
  rescaleSlotsToPeak,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  BgSimpleForecastPreview,
  ImportedDraftResult,
  KomaSlots,
  RetailerEditRow,
  RetailerFieldsSnapshot,
  SaveState,
  TargetScope,
  UnattributedEditRow,
} from '~/pages/DemandForecastDaily/models/types'

import type {
  AdoptReferenceColumnOutcome,
  UseAdoptReferenceColumnResult,
} from './useAdoptReferenceColumn'
import { useAdoptReferenceColumn } from './useAdoptReferenceColumn'
import {
  UNATTRIBUTED_SAVE_STATE_KEY,
  type UseDraftSaveQueueResult,
  useDraftSaveQueue,
} from './useDraftSaveQueue'
import { useForecastDirtyState } from './useForecastDirtyState'

// ── types ──────────────────────────────────────────────────────────────

type UseForecastDraftEditorParams = {
  page: DemandForecastDailyPage | undefined
  caseId: string | undefined
  selectedAreaCode: AreaCode | undefined
  section: WorkflowSection
  targetDate: ProtoDate | undefined
  selectedRetailerBusinessCd: string | undefined
  targetScope: TargetScope
  gcKoma: number
  dragMode: 0 | 1
  refetchPage: () => Promise<unknown>
  /**
   * 集計表示 (BG全体 × 配下複数) の編集で、全内訳が 0 のため変更を受け付けな
   * かったコマがあった時の通知 (AC-DFD-054a)。呼び出し元が文言を表示する。
   */
  onAggregateZeroRejected?: (komas: number[]) => void
}

/**
 * The display scope remains BG for a single-breakdown BG view, while its write
 * target follows the current draft's unattributed remainder (AC-DFD-047c).
 */
type EffectiveWriteTarget =
  | { scope: 'BG_TOTAL' }
  | { scope: 'UNATTRIBUTED' }
  | { scope: 'RETAILER'; retailerBusinessCd: string | undefined }

export type UseForecastDraftEditorResult = {
  editRows: Record<string, RetailerEditRow>
  hasUnattributedRemaining: boolean
  effectiveWriteTarget: EffectiveWriteTarget
  aggregateEdit: { unfilledRetailerNames: string[]; canEdit: boolean }
  unattributedBaselineSlots: KomaSlots | undefined
  /** The non-persisted unattributed preview buffer used in BG total display. */
  unattributedSlots: KomaSlots | undefined
  saveStates: Record<string, SaveState>
  bgTotalLiveSlots: KomaSlots
  currentTarget:
    | { kind: 'bgTotal' }
    | { kind: 'unattributed' }
    | { kind: 'retailer'; row: RetailerEditRow }
  displaySlots: KomaSlots
  displayLabel: string
  currentEditRow: RetailerEditRow | undefined
  currentSaveState: SaveState | undefined
  /**
   * 選択中の小売の保存失敗時の原因別文言 (currentSaveState==='error' の時のみ
   * 意味を持つ)。reason を解決できなかった場合は undefined (呼び出し元は既存の
   * code ベース fallback 文言を表示する)。
   */
  currentSaveErrorMessage: string | undefined
  /** 選択中の小売の dirty コマ集合 (AC-02: 変更コマの視覚区別)。BG全体ビュー時は空集合 */
  currentDirtyKomas: Set<number>
  /**
   * 選択中小売の編集行が dirty か (値差 or 取込元メタ差、= `isRowDirty` の full
   * 判定)。変更破棄 (canDiscard) の唯一の判定源。dirty コマが空でも取込元メタ
   * のみ差がある場合を含むため currentDirtyKomas とは別に公開する。BG全体
   * (複数小売) ビュー時は false。
   */
  currentRowDirty: boolean
  /**
   * 全 editRows の dirtyKomas の和集合 (spec AC-039 前段: BG全体〈複数小売〉
   * ビュー用)。currentEditRow が undefined になる BG_TOTAL 表示時は
   * currentDirtyKomas が空集合になり変更コマの視覚区別が出ないため、集約ビュー
   * 専用にこちらを公開する。Σ小売 = BG のため「いずれかの小売が dirty なコマ =
   * 集約値が変わったコマ」で意味論は一意 (review 指摘)。
   */
  bgTotalDirtyKomas: Set<number>
  /** 画面全体で 1 つでも dirty な小売があるか (AC-039: 保存ボタン活性の判定源) */
  anyDirty: boolean
  /**
   * 画面全体で 1 つでも楽観ロック衝突 (saveState === 'conflict') の小売が
   * あるか (AC-09: BG_TOTAL〈複数小売〉ビューは currentSaveState が
   * selectedRetailerBusinessCd 依存で undefined になるため、衝突通知・「最新を
   * 読み込み直す」導線の表示フォールバックに使う、review 指摘 PR #3199)。
   */
  anyConflict: boolean
  /** dirty または保存中の小売が存在するか (AC-12: 確定ボタン gate) */
  canConfirm: boolean
  hasInFlight: boolean
  editRowList: RetailerEditRow[]
  /** 最終保存時刻 (表示用、設計書 §4「最終保存 M/D HH:mm」表示) */
  lastSavedAt: Date | undefined
  /**
   * KomaGrid セル直接編集 (spec AC-DFD-001/006、単一セル手入力専用)。補正モード=
   * 比例配分 (dragMode=1) では編集コマ以降を編集前値比で比例配分する (AC-DFD-006
   * ※「本補正モードは 48 コマテーブルのセル編集にも適用する」)。旧シフト複数選択の
   * 一括補正 (旧 AC-14) は demand-fix-edit-operation で撤去済み。貼付は
   * `handlePaste` を使う (本関数は通さない)。
   */
  handleKomaChange: (
    koma: number,
    value: number | undefined,
    nextSlots: KomaSlots,
  ) => void
  /**
   * KomaGrid 貼付 (spec AC-DFD-006「貼付は補正モードを問わず貼付範囲のみ上書き」):
   * 比例配分をバイパスし、貼付範囲 (nextSlots) をそのまま forecastSlots へ反映する。
   * `handleKomaChange` (単一セル編集、比例配分の対象) とは意図的に別関数にする
   * (demand-fix-edit-operation Decision #1: signature を分けることで比例配分を
   * バイパスする意図を可読化する)。
   */
  handlePaste: (startKoma: number, nextSlots: KomaSlots) => void
  /**
   * チャートドラッグ編集 (spec AC-006)。dragMode=1 (以降を比例配分) のときのみ
   * applyProportional を適用する。KomaGrid セル編集とは意図的に別関数
   * (AC-02 の「比例配分はチャートドラッグ時のみ作用する」を型で分離)。
   */
  handleChartDragEdit: (
    koma: number,
    value: number,
    nextSlots: KomaSlots,
  ) => void
  /**
   * 変更を破棄 (共通仕様 demand-forecast-common.md AC-DFC-EDIT-002): 未保存の
   * 編集を破棄し、直近の保存済み状態 (baseline) に戻す。呼び出し元
   * (DiscardConfirmModal) が確認ダイアログを経由する。AC-13: 自動保存は発火しない
   * (バッファ更新のみ)。
   */
  handleDiscardChanges: () => void
  /**
   * 最大電力補正 (共通仕様 demand-forecast-common.md AC-DFC-EDIT-001): 48 コマの
   * 需要想定値全体を比例で拡大・縮小し、ピーク値が目標値に一致するよう変換する。
   * GC 済みコマは変更対象外。
   */
  handleRescaleApply: (targetPeakMw: number) => void
  /**
   * 実績トレンド反映 (demand-trend-reflection-single-authority design D3:
   * backend 産出の retailer 別補正系列を editRow へ proto→koma 変換して
   * そのまま代入するだけ — 乗算・round・GC 判定は行わない、backend
   * `TrendReflectionPreview.retailer_corrected_slots` が単一 authority)。
   * 空配列 (= backend から系列を受け取れなかった) の場合は何もしない
   * (caller 側でダイアログを閉じる等の UI 操作を行う)。retailer_corrected_slots
   * に対応 entry の無い editRow は上書きせず不変で残す (実装上の必須ガード:
   * deploy skew 等で系列が一部欠落しても需要想定を消失させない)。AC-04:
   * 反映対象コマを manualEditKomas にマークしない (旧実装は
   * markEditableKomasManual を merge していたが削除)。
   */
  handleTrendReflectApply: (
    retailerCorrectedSlots: RetailerCorrectedSlots[],
  ) => void
  /**
   * 参照列 (過去実績／確定履歴) の「採用」ボタン押下 (spec AC-DFD-047/047a/047b,
   * AC-DFD-048/048a、design note tmp/designing-feature/demand-adopt-reference-
   * column.md)。`column.key` が現在の採用中列 (`adoptedReferenceColumnKey`) と
   * 一致する場合はトグルとして「解除」(採用直前 snapshot へ復元、Decision 1) を
   * 行い、それ以外は「採用」(参照列の値を反映、Decision 2/4/5) を行う。
   * `column.sourceLabel` は呼び出し元 (KomaGrid) が Decision 5 の書式
   * (過去実績列「過去実績 <日付>」／確定履歴列は既存 label そのまま) で
   * 算出済みの文字列を渡す。
   */
  handleAdoptReferenceColumn: (column: {
    key: string
    sourceLabel: string
    slots: KomaSlots
  }) => AdoptReferenceColumnOutcome
  /**
   * 選択中小売の「採用中」参照列キー (AC-DFD-047b: 同時に採用中は最大 1 列)。
   * undefined = 採用中の列なし。保存 (Decision 3) / 破棄 / 衝突解消でクリアされる。
   */
  adoptedReferenceColumnKey: string | undefined
  /**
   * BG 全体×配下複数の per-retailer 採用 (AC-DFD-047d / 048)。
   * 呼び出し元 (index.tsx) が過去実績の帰属値・確定履歴参照列の bgResults を
   * 現在 BG 配下の小売に絞り込んで渡す。undo/snapshot の意味論は
   * `useAdoptReferenceColumn` 参照。
   */
  handleAdoptReferenceColumnForRetailers: UseAdoptReferenceColumnResult['handleAdoptReferenceColumnForRetailers']
  /**
   * 集計表示の「採用中」列 key (AC-DFD-047b、BG 集計ヘッダーの「採用中」表示 SoT)。
   */
  resolveAggregateAdoptedKey: () => string | undefined
  /**
   * 「保存」ボタン (AC-038/039/040): dirty な全小売分の SaveDemandForecastDraft
   * を呼ぶ。全て成功した場合のみ `{ kind: 'sent', ok: true }` を返す (失敗した小売のみ
   * dirty 残留、AC-042)。上限超過 (AC-DFC-EDIT-004) は 1 件も送らず `limitRejected`。
   */
  executeSaveAll: UseDraftSaveQueueResult['executeSaveAll']
  /** dirty な全小売のバッファを baseline (= 直近の読込/保存値) へ破棄する (AC-07「破棄して移動」)。 */
  discardAllDirty: () => void
  /**
   * 楽観ロック衝突 (ABORTED) からの復旧 (AC-09): 対象小売 (複数可) のバッファを
   * baseline へ破棄し、最新データを再取得する。ユーザー操作でのみ呼ばれる
   * (自動 refetch はしない)。BG_TOTAL (複数小売) ビューで複数小売が同時に
   * ABORTED になるケースに対応するため retailerBusinessCd の配列を受け取る (review 指摘
   * PR #3199 修正1: 単一 retailerBusinessCd 版は先頭 1 件しか解決できず、残りの conflict 行が
   * 古いバッファ + stale な draftUpdatedAt のまま残り再保存できなかった)。
   * refetchPage() は全 retailerBusinessCd の revert 後に 1 回だけ呼ぶ。
   */
  resolveConflicts: (retailerBusinessCds: string[]) => Promise<void>
  /**
   * §7 簡易予測プレビュー (RETAIL scope) 応答を editRows へ反映する。全スロット・
   * provider・provider_label を上書きし、manual_edit_komas は空リセットする。
   * baseline は更新しない (= 全コマ dirty 化して保存ボタンを active にする、AC-10)。
   * §5 確定履歴の参照列プレビュー (PreviewConfirmRecord) は draft を一切
   * 更新しないためこの関数を呼ばない (confirmRecordRef.ts が別に参照列 state を持つ)。
   */
  applyImportedDraft: (
    retailerBusinessCd: string,
    result: ImportedDraftResult,
  ) => boolean
  /**
   * §7 簡易予測プレビュー BG_TOTAL scope 応答 (bg_results) を複数小売の
   * editRows へ一括反映する (ADR demand-forecast-scope-scale-confirmed-share-basis)。
   * RETAIL preview (applyImportedDraft) と同様に取込原値・provider 系を丸ごと
   * 更新し manualEditKomas を空リセットする。preview は書込みが無いため baseline
   * は更新しない (AC-10)。
   */
  applyPreviewSimpleForecastBgResults: (
    preview: BgSimpleForecastPreview,
  ) => boolean
}

/**
 * view (KomaGrid / ForecastCard / 各 dialog flow) が消費する editor の表示 state /
 * buffer-write ハンドラだけを絞り込んだ projection。`useForecastPageModel` はこの型で
 * editor を nested value として公開し、view から editor 全 field へ到達させない。
 *
 * ここに含めないもの (page-model の orchestration が wrap している raw 側):
 * - `executeSaveAll` / `discardAllDirty` / `resolveConflicts` — raw write 入口
 *   (page-model の `handleSave` / `handleDiscardAndLeave` / `handleResolveConflict` が
 *   toast・過去日 guard・BG_TOTAL 一括解決を付けて wrap する)
 * - `saveStates` / `currentSaveState` / `currentDirtyKomas` / `bgTotalDirtyKomas` /
 *   `anyConflict` — fallback 前の raw 値 (page-model の `deriveForecastGates` が
 *   `effectiveDirtyKomas` / `effectiveSaveState` へ畳んでから公開する)
 * - `bgTotalLiveSlots` / `currentTarget` — page-model 内部の派生専用
 *
 * この絞り込みを editor 側 (自身の view projection の所有者) に co-locate することで、
 * editor の field rename は本 Pick の更新で閉じ、page-model の public interface
 * (`editor: ForecastEditorViewModel`) は churn しない。
 */
export type ForecastEditorViewModel = Pick<
  UseForecastDraftEditorResult,
  | 'editRows'
  | 'displaySlots'
  | 'displayLabel'
  | 'currentEditRow'
  | 'currentSaveErrorMessage'
  | 'editRowList'
  | 'anyDirty'
  | 'hasInFlight'
  | 'lastSavedAt'
  | 'canConfirm'
  | 'handleKomaChange'
  | 'handlePaste'
  | 'handleChartDragEdit'
  | 'handleDiscardChanges'
  | 'handleRescaleApply'
  | 'handleTrendReflectApply'
  | 'handleAdoptReferenceColumn'
  | 'adoptedReferenceColumnKey'
  | 'handleAdoptReferenceColumnForRetailers'
  | 'resolveAggregateAdoptedKey'
  | 'applyImportedDraft'
  | 'applyPreviewSimpleForecastBgResults'
>

// ── helpers ────────────────────────────────────────────────────────────

/** row の現在値を baseline (= row.baseline) へ戻したコピーを返す (破棄操作の共通部)。 */
function revertToBaseline(row: RetailerEditRow): RetailerEditRow {
  return {
    ...row,
    ...row.baseline,
    manualEditKomas: new Set(row.baseline.manualEditKomas),
  }
}

function revertUnattributedToBaseline(
  row: UnattributedEditRow,
): UnattributedEditRow {
  return {
    ...row,
    ...row.baseline,
    manualEditKomas: new Set(row.baseline.manualEditKomas),
  }
}

function protoUnattributedToEditRow(
  draft: NonNullable<
    NonNullable<UseForecastDraftEditorParams['page']>['unattributed']
  >,
): UnattributedEditRow {
  const fields: RetailerFieldsSnapshot = {
    forecastSlots: protoSlotsToKoma(draft.forecastSlots),
    providerSlots: protoSlotsToKoma(draft.providerSlots),
    manualEditKomas: new Set(draft.manualEditKomas),
    provider: draft.provider,
    providerOther: draft.providerOther,
    providerLabel: draft.providerLabel,
  }
  return {
    ...fields,
    draftUpdatedAt: draft.draftUpdatedAt,
    baseline: fieldsSnapshotOf(fields),
  }
}

const SIMPLE_FORECAST_SLOT_COUNT = 48

function isCompleteSlots(slots: ImportedDraftResult['forecastSlots']): boolean {
  return (
    slots.length === SIMPLE_FORECAST_SLOT_COUNT &&
    protoSlotsToKoma(slots).every(
      (value) => value !== undefined && Number.isFinite(value),
    )
  )
}

function isForecastSlotsCompleteFromGc(
  slots: ImportedDraftResult['forecastSlots'],
  currentGcKoma: number,
): boolean {
  if (slots.length !== SIMPLE_FORECAST_SLOT_COUNT) return false

  const parsedSlots = protoSlotsToKoma(slots)
  return parsedSlots.every((value, index) => {
    if (slots[index]?.value === undefined) return index < currentGcKoma
    return value !== undefined && Number.isFinite(value)
  })
}

function canApplyBgSimpleForecastPreview(
  preview: BgSimpleForecastPreview,
  currentRetailerBusinessCds: string[],
  currentGcKoma: number,
): boolean {
  if (
    !isForecastSlotsCompleteFromGc(
      preview.unattributed.forecastSlots,
      currentGcKoma,
    ) ||
    !isCompleteSlots(preview.unattributed.providerSlots) ||
    preview.retailerResults.length !== currentRetailerBusinessCds.length
  )
    return false

  const expected = new Set(currentRetailerBusinessCds)
  const received = new Set<string>()
  for (const result of preview.retailerResults) {
    if (
      received.has(result.retailerBusinessCd) ||
      !expected.has(result.retailerBusinessCd) ||
      !isForecastSlotsCompleteFromGc(result.forecastSlots, currentGcKoma) ||
      !isCompleteSlots(result.providerSlots)
    )
      return false
    received.add(result.retailerBusinessCd)
  }
  return received.size === expected.size
}

function mergePreviewSlotsAtCurrentGc(
  currentSlots: KomaSlots,
  previewSlots: ImportedDraftResult['forecastSlots'],
  currentGcKoma: number,
): KomaSlots {
  const nextSlots = protoSlotsToKoma(previewSlots)
  return nextSlots.map((value, index) =>
    index < currentGcKoma ? currentSlots[index] : value,
  )
}

// ── hook ───────────────────────────────────────────────────────────────

export function useForecastDraftEditor({
  page,
  caseId,
  selectedAreaCode,
  section,
  targetDate,
  selectedRetailerBusinessCd,
  targetScope,
  gcKoma,
  dragMode,
  refetchPage,
  onAggregateZeroRejected,
}: UseForecastDraftEditorParams): UseForecastDraftEditorResult {
  // ── 過去日 write-gate (PR #5066 review C-1) ───────────────────────
  //
  // 過去日は全ての値変更操作を封鎖する (conventions.md §GC ロック /
  // principles.md 鉄則1「表示と実行の乖離は許容しない」)。render 時計算の
  // 表示 gate (index.tsx の canMutateTargetDate) は、画面を開いたまま
  // JST 0:00 を跨ぐと親再レンダーまで stale (= 当日扱い) のまま残るため、
  // 書込の唯一の seam である本 hook の各書込入口で実時刻から再判定して
  // no-op にする (defense in depth を write boundary に集約。従来は index.tsx
  // 側で各ハンドラを個別 wrapper 封鎖していたのを畳み込んだ)。判定式は
  // models/canMutate へ集約済み (5 seam を単一 predicate 経由に統一)。
  // 表示・disabled 側の第一防御 (canMutateTargetDate) は index.tsx に残す。
  const canMutateNow = useCallback(
    () => targetScope !== 'UNATTRIBUTED' && canMutate(targetDate),
    [targetDate, targetScope],
  )

  // ── state ──────────────────────────────────────────────────────────

  const [editRows, setEditRows] = useState<Record<string, RetailerEditRow>>({})
  const [unattributedRow, setUnattributedRow] = useState<
    UnattributedEditRow | undefined
  >(undefined)
  const hasUnattributedRemaining =
    unattributedRow?.forecastSlots.some(
      (value) => value !== undefined && value !== 0,
    ) ??
    (page?.unattributed !== undefined &&
      protoSlotsToKoma(page.unattributed.forecastSlots).some(
        (value) => value !== undefined && value !== 0,
      ))
  const effectiveWriteTarget = useMemo<EffectiveWriteTarget>(() => {
    const retailerBusinessCds = Object.keys(editRows)
    if (targetScope === 'BG_TOTAL' && retailerBusinessCds.length === 1) {
      return hasUnattributedRemaining
        ? { scope: 'BG_TOTAL' }
        : {
            scope: 'RETAILER',
            retailerBusinessCd: retailerBusinessCds[0],
          }
    }
    if (targetScope === 'BG_TOTAL') return { scope: 'BG_TOTAL' }
    if (targetScope === 'UNATTRIBUTED') return { scope: 'UNATTRIBUTED' }
    return { scope: 'RETAILER', retailerBusinessCd: selectedRetailerBusinessCd }
  }, [
    editRows,
    hasUnattributedRemaining,
    selectedRetailerBusinessCd,
    targetScope,
  ])
  const aggregateEdit = deriveAggregateEdit(
    targetScope,
    (page?.retailers.length ?? 0) > 1,
    hasUnattributedRemaining,
    editRows,
    gcKoma,
  )
  const effectiveRetailerBusinessCd =
    effectiveWriteTarget.scope === 'RETAILER'
      ? effectiveWriteTarget.retailerBusinessCd
      : undefined
  const gcKomaRef = useRef(gcKoma)
  gcKomaRef.current = gcKoma
  const importContextRef = useRef({
    page,
    targetDate,
    editRows,
    unattributedRow,
  })
  importContextRef.current = { page, targetDate, editRows, unattributedRow }
  // Use the shared server-frontier model at application time, including when
  // no render or fetch occurred while the preview RPC was pending.
  const currentImportGcKoma = useCallback((): number => {
    const context = importContextRef.current
    const frontier = gateCloseFrontierFromProto(context.page?.gateCloseFrontier)
    const date = protoDateToCivilDate(context.targetDate)
    if (frontier === undefined || date === undefined) return gcKomaRef.current
    const countdown = gateCloseCountdownOn(frontier, date)
    const advanced =
      countdown.status === 'allClosed'
        ? 48
        : countdown.status === 'notStarted'
          ? 0
          : countdown.koma - 1
    return Math.max(gcKomaRef.current, advanced)
  }, [])

  // ── editRows 初期化 ───────────────────────────────────────────────
  //
  // dirty な行 (= baseline と現在値が異なる) は page 再取得時も保持する (未保存の
  // 変更を GetDemandForecastDaily の再フェッチで黙って消さない)。

  const prevPageRef = useRef<typeof page>(undefined)
  useEffect(() => {
    if (page === undefined) return
    if (prevPageRef.current === page) return
    prevPageRef.current = page

    setEditRows((prev) => {
      const next: Record<string, RetailerEditRow> = {}
      for (const retailer of page.retailers) {
        const existing = prev[retailer.retailerBusinessCd]
        const existingIsDirty = existing !== undefined && isRowDirty(existing)
        next[retailer.retailerBusinessCd] = existingIsDirty
          ? existing
          : protoRetailerToEditRow(retailer)
      }
      return next
    })
    setUnattributedRow((prev) =>
      page.unattributed === undefined
        ? undefined
        : prev !== undefined && isRowDirty(prev)
          ? prev
          : protoUnattributedToEditRow(page.unattributed),
    )
  }, [page])

  // ── 参照列採用 (undo state machine) / save queue (並行性) ────────────
  //
  // 最高 risk の 2 クラスタは headless hook に分離した (architecture review C4)。
  // editRows を軸に本 hook が合成する: adopt は採用中 undo を所有し editRows を
  // 反映、save queue は saveStates / 直列化 / 楽観ロックを所有し保存成功で
  // editRows.baseline を更新する。save queue → adopt の Decision 3 連携 (保存で
  // 採用中 undo をクリア) は onSaveStart/onSaved の callback で結ぶ。
  const adopt = useAdoptReferenceColumn({
    editRows,
    setEditRows,
    unattributedRow,
    setUnattributedRow,
    selectedRetailerBusinessCd: effectiveRetailerBusinessCd,
    gcKoma,
    canMutateNow,
  })

  const saveQueue = useDraftSaveQueue({
    unattributedRow,
    setUnattributedRow,
    editRows,
    setEditRows,
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    canMutateNow,
    gcKoma,
    onSaveStart: adopt.captureAdoptedEntry,
    onSaved: adopt.clearAdoptedEntryIfUnchanged,
  })

  // ── discardAllDirty (AC-07「破棄して移動」) ──────────────────────

  const discardAllDirty = useCallback((): void => {
    // revertedRetailerBusinessCds は setEditRows の外 (= 現在の editRows state)
    // から同期的に求める。setEditRows の functional updater 内で配列を mutate
    // して呼び出し側で読もうとすると、React は updater をこの関数呼び出しの
    // 場では実行しない (次の再レンダー時に遅延実行) ため、直後の
    // `if (revertedRetailerBusinessCds.length > 0)` は常に空配列を見てしまい
    // 採用中 undo state のクリアが no-op になる (discardAllDirty 経由の
    // AC-07「破棄して移動」で発覚したバグ、以前の実装の回帰)。
    const revertedRetailerBusinessCds = Object.entries(editRows)
      .filter(([, row]) => isRowDirty(row))
      .map(([retailerBusinessCd]) => retailerBusinessCd)
    // 帰属不明も集計採用の undo entry を持つ (AC-DFD-047c、issue #11461)。
    if (unattributedRow !== undefined && isRowDirty(unattributedRow))
      revertedRetailerBusinessCds.push(UNATTRIBUTED_SAVE_STATE_KEY)
    setEditRows((prev) => {
      const next: Record<string, RetailerEditRow> = {}
      for (const [retailerBusinessCd, row] of Object.entries(prev)) {
        next[retailerBusinessCd] = isRowDirty(row) ? revertToBaseline(row) : row
      }
      return next
    })
    setUnattributedRow((row) =>
      row !== undefined && isRowDirty(row)
        ? revertUnattributedToBaseline(row)
        : row,
    )
    saveQueue.clearSaveStatesForDiscard()
    // Decision 3 (参照列「採用」): 破棄で採用中の undo state も解除する。
    adopt.clearAdopted(revertedRetailerBusinessCds)
  }, [editRows, unattributedRow, saveQueue, adopt])

  // ── resolveConflicts (AC-09) ──────────────────────────────────────

  const resolveConflicts = useCallback(
    async (retailerBusinessCds: string[]): Promise<void> => {
      if (retailerBusinessCds.length === 0) return
      const resolvesUnattributed = retailerBusinessCds.includes(
        UNATTRIBUTED_SAVE_STATE_KEY,
      )
      setEditRows((prev) => {
        const next = { ...prev }
        for (const retailerBusinessCd of retailerBusinessCds) {
          const row = next[retailerBusinessCd]
          if (row === undefined) continue
          next[retailerBusinessCd] = revertToBaseline(row)
        }
        return next
      })
      if (resolvesUnattributed) {
        setUnattributedRow((row) =>
          row !== undefined && isRowDirty(row)
            ? revertUnattributedToBaseline(row)
            : row,
        )
      }
      saveQueue.markResolved(retailerBusinessCds)
      // Decision 3 (参照列「採用」): baseline へ復元するため、対象小売の
      // 採用中 undo state も解除する (discardAllDirty/handleDiscardChanges と
      // 同じ revertToBaseline 経路)。
      adopt.clearAdopted(retailerBusinessCds)
      await refetchPage()
    },
    [refetchPage, saveQueue, adopt],
  )

  // ── 集計表示 (BG全体 × 配下複数) の直接編集 (AC-DFD-004d / 054 / 054a) ──
  //
  // AC-DFD-054: 集計値の変更は各内訳の現在値の比率で配分する。

  const aggregateBreakdowns = useCallback(():
    | AggregateBreakdown[]
    | undefined => {
    if (effectiveWriteTarget.scope !== 'BG_TOTAL') return undefined
    if (!aggregateEdit.canEdit) return undefined
    return Object.values(editRows)
  }, [aggregateEdit.canEdit, effectiveWriteTarget.scope, editRows])

  const applyAggregateSlots = useCallback(
    (slotsByRetailer: Map<string, KomaSlots>): void => {
      if (slotsByRetailer.size === 0) return
      setEditRows((prev) => {
        const next = { ...prev }
        for (const [retailerBusinessCd, slots] of slotsByRetailer) {
          const row = prev[retailerBusinessCd]
          if (row === undefined) continue
          // 値が実際に変わったコマだけ手修正扱いにする (handleKomaChange と同旨)。
          const newManual = new Set(row.manualEditKomas)
          for (let i = 0; i < 48; i++) {
            if (slots[i] !== row.forecastSlots[i]) newManual.add(i + 1)
          }
          next[retailerBusinessCd] = {
            ...row,
            forecastSlots: slots,
            manualEditKomas: newManual,
          }
        }
        return next
      })
    },
    [],
  )

  /** 集計値の変更 (現在の集計値 → 変更後の集計値) を内訳へ按分して反映する。 */
  const editAggregate = (
    toNextTotal: (total: KomaSlots) => KomaSlots,
  ): void => {
    const breakdowns = aggregateBreakdowns()
    if (breakdowns === undefined) return
    const total = calcBgTotalSlots(breakdowns.map((b) => b.forecastSlots))
    const { slotsByRetailer, rejectedKomas } = distributeAggregateSlots(
      breakdowns,
      toNextTotal(total),
      gcKoma,
    )
    if (rejectedKomas.length > 0) onAggregateZeroRejected?.(rejectedKomas)
    applyAggregateSlots(slotsByRetailer)
  }

  // ── handleKomaChange (KomaGrid 単一セル手入力、AC-DFD-006) ──────────
  //
  // spec AC-DFD-006「変化率 (= 新しい値 ÷ 編集前の値) を、そのコマ以降の各コマに
  // 乗じて補正する」「※ 本補正モードは 48 コマテーブルのセル編集にも適用する」に
  // 従い、補正モード=比例配分 (dragMode=1) の単一セル編集では編集コマ以降を
  // 「編集前値 (= 現在の需要想定値 row.forecastSlots)」比で上書きする。基準は
  // 取込原値 (providerSlots) ではなく編集前値 (spec ※「補正の基準値は編集開始
  // 時点の需要想定値とする」)。取込原値基準だと未取込 (手入力のみ) の時に比率を
  // 算出できず全く配分されない。GC ロックコマ・値クリア (value===undefined) は
  // applyProportional の対象外。旧シフト複数選択の一括補正 (旧 AC-14) は
  // demand-fix-edit-operation で撤去済み。貼付 (Ctrl+V) は本関数を通さず
  // `handlePaste` を使う (spec AC-DFD-006「貼付は補正モードを問わず貼付範囲のみ
  // 上書き」= 比例配分の対象外、下記 handlePaste 参照)。

  const handleKomaChange = (
    koma: number,
    value: number | undefined,
    nextSlots: KomaSlots,
  ): void => {
    if (!canMutateNow()) return
    if (effectiveWriteTarget.scope === 'BG_TOTAL') {
      // 補正モード=比例配分は集計値に対して適用し、その結果を内訳へ按分する。
      editAggregate((total) =>
        dragMode === 1 && value !== undefined
          ? applyProportionalToTotal(total, nextSlots, koma, value, gcKoma)
          : nextSlots,
      )
      return
    }
    if (effectiveWriteTarget.scope !== 'RETAILER') return
    const retailerBusinessCd = effectiveWriteTarget.retailerBusinessCd
    if (retailerBusinessCd === undefined) return
    setEditRows((prev) => {
      const row = prev[retailerBusinessCd]
      if (row === undefined) return prev

      const finalSlots =
        dragMode === 1 && value !== undefined
          ? applyProportional(row.forecastSlots, nextSlots, koma, value, gcKoma)
          : nextSlots

      // finalSlots (比例配分適用後) と現在値を比較し、実際に変わったコマ全てを
      // manualEditKomas に追加する (単一編集・比例配分の両対応)。
      const newManual = new Set(row.manualEditKomas)
      for (let i = 0; i < 48; i++) {
        if (finalSlots[i] !== row.forecastSlots[i]) newManual.add(i + 1)
      }
      newManual.add(koma)

      return {
        ...prev,
        [retailerBusinessCd]: {
          ...row,
          forecastSlots: finalSlots,
          manualEditKomas: newManual,
        },
      }
    })
  }

  // ── handlePaste (KomaGrid 貼付、AC-DFD-006) ─────────────────────────
  //
  // spec AC-DFD-006「貼付は補正モードを問わず貼付範囲のみ上書き」に従い、
  // dragMode に関わらず applyProportional (比例配分) を一切通さず、貼付確定済み
  // の nextSlots (KomaGrid の貼付処理が GC ロック除外を担う) をそのまま
  // forecastSlots へ反映する。handleKomaChange (単一セル編集) とは意図的に別関数
  // にすることで、貼付が比例配分をバイパスすることを型で保証する
  // (demand-fix-edit-operation Decision #1)。

  const handlePaste = (_startKoma: number, nextSlots: KomaSlots): void => {
    if (!canMutateNow()) return
    if (effectiveWriteTarget.scope === 'BG_TOTAL') {
      // 貼付は補正モードを問わず貼付範囲の集計値だけを変え、内訳へ按分する。
      editAggregate(() => nextSlots)
      return
    }
    if (effectiveWriteTarget.scope !== 'RETAILER') return
    const retailerBusinessCd = effectiveWriteTarget.retailerBusinessCd
    if (retailerBusinessCd === undefined) return
    setEditRows((prev) => {
      const row = prev[retailerBusinessCd]
      if (row === undefined) return prev

      // handleKomaChange と同じ semantic: 実際に値が変わったコマのみ
      // manualEditKomas に追加する (startKoma の無条件追加はしない — 貼付範囲の
      // 先頭が偶然編集前値と同じだった場合まで manual 化すると、実質無変更の
      // 貼付でも「手修正あり」を誤って永続化してしまう)。
      const newManual = new Set(row.manualEditKomas)
      for (let i = 0; i < 48; i++) {
        if (nextSlots[i] !== row.forecastSlots[i]) newManual.add(i + 1)
      }

      return {
        ...prev,
        [retailerBusinessCd]: {
          ...row,
          forecastSlots: nextSlots,
          manualEditKomas: newManual,
        },
      }
    })
  }

  // ── handleChartDragEdit (チャートドラッグ編集、AC-DFD-006) ───────────
  //
  // dragMode=1 (以降を比例配分) のとき applyProportional を適用する。基準は
  // 編集前値 (= ドラッグ開始時点の需要想定値 row.forecastSlots)。ドラッグ中は
  // テーブル (editRows.forecastSlots) を更新せず mouseup でこの関数を 1 回だけ
  // 呼ぶ (ForecastChart のプレビューは local state) ため、この時点の
  // row.forecastSlots はドラッグ開始時点の値 = spec ※「補正の基準値は編集開始
  // 時点の需要想定値とする (連続ドラッグでも前回の補正結果に累積させない)」に
  // 一致する。取込原値 (providerSlots) は基準にしない (未取込時に配分されない)。
  // manualEditKomas は比例配分後の finalSlots と編集前値の差分から求める
  // (providerSlots の有無に依存させない)。

  const handleChartDragEdit = (
    koma: number,
    value: number,
    nextSlots: KomaSlots,
  ): void => {
    if (!canMutateNow()) return
    if (effectiveWriteTarget.scope === 'BG_TOTAL') {
      editAggregate((total) =>
        dragMode === 1
          ? applyProportionalToTotal(total, nextSlots, koma, value, gcKoma)
          : nextSlots,
      )
      return
    }
    if (effectiveWriteTarget.scope !== 'RETAILER') return
    const retailerBusinessCd = effectiveWriteTarget.retailerBusinessCd
    if (retailerBusinessCd === undefined) return
    setEditRows((prev) => {
      const row = prev[retailerBusinessCd]
      if (row === undefined) return prev

      const finalSlots =
        dragMode === 1
          ? applyProportional(row.forecastSlots, nextSlots, koma, value, gcKoma)
          : nextSlots

      const newManual = new Set(row.manualEditKomas)
      for (let i = 0; i < 48; i++) {
        if (finalSlots[i] !== row.forecastSlots[i]) newManual.add(i + 1)
      }
      newManual.add(koma)

      return {
        ...prev,
        [retailerBusinessCd]: {
          ...row,
          forecastSlots: finalSlots,
          manualEditKomas: newManual,
        },
      }
    })
  }

  // ── handleAdoptReferenceColumn (参照列「採用」) ──────────────────────
  //
  // undo state machine は useAdoptReferenceColumn へ分離済み (本 hook は
  // editRows/setEditRows を渡すだけの wiring)。挙動 (Decision 1〜5) は当該 hook を参照。
  const handleAdoptReferenceColumn = adopt.handleAdoptReferenceColumn

  // ── handleDiscardChanges (共通仕様 AC-DFC-EDIT-002、AC-13) ───────────
  //
  // 未保存の編集を破棄し、直近の保存済み状態 (baseline) に戻す。
  // DiscardConfirmModal の確認ダイアログ「実行する」選択時に呼ばれる。バッファ
  // 更新のみで自動保存は発火しない (AC-13)。baseline へ戻すので dirty は解消される。

  const handleDiscardChanges = useCallback((): void => {
    // 集計表示 (AC-DFD-054) の編集は全内訳へ按分して書き込むため、破棄も全内訳を戻す。
    if (targetScope === 'BG_TOTAL') {
      discardAllDirty()
      return
    }
    if (selectedRetailerBusinessCd === undefined) return
    const retailerBusinessCd = selectedRetailerBusinessCd
    setEditRows((prev) => {
      const row = prev[retailerBusinessCd]
      if (row === undefined) return prev
      return {
        ...prev,
        [retailerBusinessCd]: revertToBaseline(row),
      }
    })
    // Decision 3 (参照列「採用」): 破棄で採用中の undo state も解除する。
    adopt.clearAdopted([retailerBusinessCd])
  }, [selectedRetailerBusinessCd, adopt, targetScope, discardAllDirty])

  // ── handleRescaleApply (共通仕様 AC-DFC-EDIT-001「最大電力補正」) ───
  //
  // 48 コマの需要想定値全体を比例で拡大・縮小し、ピーク値が目標値に一致するよう
  // 変換する (GC 済みコマは rescaleSlotsToPeak が変更対象外にする)。

  const handleRescaleApply = useCallback(
    (targetPeakMw: number): void => {
      if (!canMutateNow()) return
      if (effectiveWriteTarget.scope === 'BG_TOTAL') {
        // AC-DFD-054: 集計値のピークが目標値になる倍率を全内訳に同じく掛ける。
        const breakdowns = aggregateBreakdowns()
        if (breakdowns === undefined) return
        applyAggregateSlots(
          rescaleAggregateToPeak(breakdowns, targetPeakMw, gcKoma),
        )
        return
      }
      if (effectiveWriteTarget.scope !== 'RETAILER') return
      const retailerBusinessCd = effectiveWriteTarget.retailerBusinessCd
      if (retailerBusinessCd === undefined) return
      setEditRows((prev) => {
        const row = prev[retailerBusinessCd]
        if (row === undefined) return prev
        const newSlots = rescaleSlotsToPeak(
          row.forecastSlots,
          targetPeakMw,
          gcKoma,
        )
        const newManual = new Set([
          ...row.manualEditKomas,
          ...markEditableKomasManual(gcKoma),
        ])
        return {
          ...prev,
          [retailerBusinessCd]: {
            ...row,
            forecastSlots: newSlots,
            manualEditKomas: newManual,
          },
        }
      })
    },
    [
      gcKoma,
      canMutateNow,
      effectiveWriteTarget,
      aggregateBreakdowns,
      applyAggregateSlots,
    ],
  )

  // ── handleTrendReflectApply ───────────────────────────────────────
  //
  // demand-trend-reflection-single-authority design D3: backend が算出した
  // retailer 別補正系列を各 editRow へ proto→koma 変換して代入するだけ (乗算・
  // round・GC 判定は backend `TrendReflectionPreview.retailer_corrected_slots`
  // 側に一本化済み、frontend からは撤去)。AC-04: 実績トレンド反映で GC 以降の
  // コマを manualEditKomas にマークしない (旧実装は markEditableKomasManual
  // (gcKoma) を merge していたが削除)。

  const handleTrendReflectApply = useCallback(
    (retailerCorrectedSlots: RetailerCorrectedSlots[]): void => {
      if (!canMutateNow()) return
      if (retailerCorrectedSlots.length === 0) return

      const correctedByBusinessCd = new Map(
        retailerCorrectedSlots.map((r) => [
          r.retailerBusinessCd,
          r.correctedSlots,
        ]),
      )

      setEditRows((prev) => {
        const next = { ...prev }
        for (const retailerBusinessCd of Object.keys(prev)) {
          const row = prev[retailerBusinessCd]
          if (row === undefined) continue
          const corrected = correctedByBusinessCd.get(retailerBusinessCd)
          // 実装上の必須ガード: retailer_corrected_slots に対応 entry が無い
          // editRow は上書きせず不変で残す (deploy skew 等で系列が一部欠落
          // しても空系列で forecastSlots を潰して需要想定を消失させない)。
          if (corrected === undefined) continue
          next[retailerBusinessCd] = {
            ...row,
            forecastSlots: protoSlotsToKoma(corrected),
          }
        }
        return next
      })
    },
    [canMutateNow],
  )

  // ── applyImportedDraft (§7 簡易予測プレビュー / §6 確定履歴取込) ────
  //
  // AC-10: preview 応答は draft (baseline) を更新しない。全コマ dirty 化して
  // 保存ボタンを active にする (preview は書込みが無いため常にこの経路のみ、
  // 確定履歴の参照列プレビューはこの関数を呼ばない — 上記 doc comment 参照)。

  const applyImportedDraft = useCallback(
    (retailerBusinessCd: string, result: ImportedDraftResult): boolean => {
      const importGcKoma = currentImportGcKoma()
      if (
        !canMutateNow() ||
        !isForecastSlotsCompleteFromGc(result.forecastSlots, importGcKoma) ||
        !isCompleteSlots(result.providerSlots)
      )
        return false
      if (importContextRef.current.editRows[retailerBusinessCd] === undefined)
        return false
      setEditRows((prev) => {
        const row = prev[retailerBusinessCd]
        if (row === undefined) return prev
        const nextFields: RetailerFieldsSnapshot = {
          forecastSlots: mergePreviewSlotsAtCurrentGc(
            row.forecastSlots,
            result.forecastSlots,
            importGcKoma,
          ),
          providerSlots: protoSlotsToKoma(result.providerSlots),
          manualEditKomas: new Set(),
          provider: result.provider,
          providerOther: result.providerOther,
          providerLabel: result.providerLabel,
        }
        return {
          ...prev,
          [retailerBusinessCd]: {
            ...row,
            ...nextFields,
          },
        }
      })
      // Decision 3 (参照列「採用」): 取込は draft (forecastSlots/providerLabel) を
      // 丸ごと差し替えるため、採用中の undo state も解除する。残すと取込後も
      // 該当列が「採用中」表示のまま残り、再押下で採用直前 (取込前) の値へ復元して
      // 取り込んだ未保存値が失われる (保存/破棄/衝突解消と同じ扱い)。
      adopt.clearAdopted([retailerBusinessCd])
      return true
    },
    [canMutateNow, adopt, currentImportGcKoma],
  )

  // ── applyPreviewSimpleForecastBgResults ────────────────────────────

  const applyPreviewSimpleForecastBgResults = useCallback(
    (preview: BgSimpleForecastPreview): boolean => {
      const importGcKoma = currentImportGcKoma()
      if (!canMutateNow()) return false
      if (
        importContextRef.current.unattributedRow === undefined ||
        !canApplyBgSimpleForecastPreview(
          preview,
          Object.keys(importContextRef.current.editRows),
          importGcKoma,
        )
      )
        return false
      // §7 簡易予測プレビュー BG_TOTAL scope (review 指摘 PR #3199):
      // RETAIL preview (applyImportedDraft) と同様に取込原値・provider 系を
      // 丸ごと更新し manualEditKomas は空にリセットする (取込元=現在値のため
      // 手修正マークは空が正、dirty 表示は dirtyKomas が担う)。baseline は
      // preview のため更新しない (AC-10)。
      setEditRows((prev) => {
        if (
          !canApplyBgSimpleForecastPreview(
            preview,
            Object.keys(prev),
            importGcKoma,
          )
        )
          return prev
        const next = { ...prev }
        for (const result of preview.retailerResults) {
          const row = prev[result.retailerBusinessCd]
          // Preflight checked the complete key set above. This guard remains
          // local to the atomic updater in case the buffer changes meanwhile.
          if (row === undefined) return prev
          const nextFields = {
            forecastSlots: mergePreviewSlotsAtCurrentGc(
              row.forecastSlots,
              result.forecastSlots,
              importGcKoma,
            ),
            providerSlots: protoSlotsToKoma(result.providerSlots),
            manualEditKomas: new Set<number>(),
            provider: result.provider,
            providerOther: result.providerOther,
            providerLabel: result.providerLabel,
          }
          next[result.retailerBusinessCd] = {
            ...row,
            ...nextFields,
          }
        }
        return next
      })
      setUnattributedRow((current) => {
        if (current === undefined) return current
        return {
          ...current,
          forecastSlots: mergePreviewSlotsAtCurrentGc(
            current.forecastSlots,
            preview.unattributed.forecastSlots,
            importGcKoma,
          ),
          providerSlots: protoSlotsToKoma(preview.unattributed.providerSlots),
          manualEditKomas: new Set(),
          provider: preview.unattributed.provider,
          providerOther: preview.unattributed.providerOther,
          providerLabel: preview.unattributed.providerLabel,
        }
      })
      // Decision 3 (参照列「採用」): BG 一括取込も draft を丸ごと差し替えるため、
      // 結果に含まれる全小売と帰属不明の採用中 undo state を解除する
      // (applyImportedDraft と同旨)。
      adopt.clearAdopted([
        ...preview.retailerResults.map((result) => result.retailerBusinessCd),
        UNATTRIBUTED_SAVE_STATE_KEY,
      ])
      return true
    },
    [canMutateNow, adopt, currentImportGcKoma],
  )

  // ── 派生データ ────────────────────────────────────────────────────
  //
  // async RPC cluster (保存直列化・save/discard) と独立な純計算クラスタは
  // useForecastDirtyState へ分離した (dirty 集合・BG全体ライブ合計・表示対象の
  // 導出は editRows のみに依存し saveStates に依存しない)。saveStates 由来の
  // 派生 (currentSaveState / hasInFlight / anyConflict) はサーバー状態 (保存
  // フロー) に依存するため useDraftSaveQueue が持つ。
  const {
    editRowList,
    bgTotalLiveSlots,
    currentTarget,
    displaySlots,
    displayLabel,
    currentEditRow,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
  } = useForecastDirtyState({
    editRows,
    unattributedRow,
    bgTotalSlots: page?.bgTotalSlots,
    targetScope: effectiveWriteTarget.scope,
    selectedRetailerBusinessCd:
      effectiveWriteTarget.scope === 'RETAILER'
        ? effectiveWriteTarget.retailerBusinessCd
        : undefined,
  })

  const {
    saveStates,
    saveErrorMessages,
    lastSavedAt,
    hasInFlight,
    anyConflict,
  } = saveQueue

  const currentSaveState: SaveState | undefined =
    effectiveRetailerBusinessCd !== undefined
      ? saveStates[effectiveRetailerBusinessCd]
      : undefined

  // AC-DFC-007: 選択中小売の保存失敗を表示する。
  const currentSaveErrorMessage: string | undefined =
    effectiveRetailerBusinessCd !== undefined
      ? saveErrorMessages[effectiveRetailerBusinessCd]
      : undefined

  const canConfirm = editRowList.length > 0 && !anyDirty && !hasInFlight

  // ── return ────────────────────────────────────────────────────────

  return {
    editRows,
    hasUnattributedRemaining,
    effectiveWriteTarget,
    aggregateEdit,
    unattributedBaselineSlots: unattributedRow?.baseline.forecastSlots,
    unattributedSlots: unattributedRow?.forecastSlots,
    saveStates,
    bgTotalLiveSlots,
    currentTarget,
    displaySlots,
    displayLabel,
    currentEditRow,
    currentSaveState,
    currentSaveErrorMessage,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
    anyConflict,
    canConfirm,
    hasInFlight,
    editRowList,
    lastSavedAt,
    handleKomaChange,
    handlePaste,
    handleChartDragEdit,
    handleDiscardChanges,
    handleRescaleApply,
    handleTrendReflectApply,
    handleAdoptReferenceColumn,
    adoptedReferenceColumnKey: adopt.adoptedReferenceColumnKey,
    handleAdoptReferenceColumnForRetailers:
      adopt.handleAdoptReferenceColumnForRetailers,
    resolveAggregateAdoptedKey: adopt.resolveAggregateAdoptedKey,
    executeSaveAll: saveQueue.executeSaveAll,
    discardAllDirty,
    resolveConflicts,
    applyImportedDraft,
    applyPreviewSimpleForecastBgResults,
  }
}
